import type { AxiosInstance } from 'axios'
import type {
  AcademyLessonStatus,
  AcademyRevisionStatus,
  IAcademyChapter,
  IAcademyManagedLesson,
  IAcademyRevision,
  IFactoryFunctionRequest,
} from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import useAxios from '@/services/api/useAxios'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import type {
  TAcademyChapterPayload,
  TAcademyLessonPayload,
  TAcademyManagedLessonWithProduction,
  TAcademyMediaUpload,
  TAcademyProducePayload,
  TAcademyPublishPayload,
  TAcademyRevisionDetail,
  TAcademyRevisionPayload,
  TAcademyRun,
  TAcademyRunsResponse,
  TSandboxCompaniesReport,
} from '@/services/academy/types'

/** BestConsultant team only (management page `ManagementAcademy`): the catalog shared by every firm. */
const baseUrl = () => `${BASE_URL}/${API_MAIN_VERSION}/academy/management`

const withoutEmpty = (query: Record<string, string | undefined>) => Object.fromEntries(
  Object.entries(query).filter(([, value]) => value !== undefined && value !== ''),
) as Record<string, string>

// Chapters

const chapters = (axios: IFactoryFunctionRequest<IAcademyChapter[], any>) => () => {
  return axios({
    method: EMethod.get,
    url: `${baseUrl()}/chapters`,
  })
}

const createChapter = (axios: IFactoryFunctionRequest<IAcademyChapter, TAcademyChapterPayload>) => (data: TAcademyChapterPayload) => {
  return axios({
    method: EMethod.post,
    url: `${baseUrl()}/chapters`,
    data,
  })
}

/** The key cannot change: lessons reference the chapter by it. */
const updateChapter = (axios: IFactoryFunctionRequest<IAcademyChapter, Omit<TAcademyChapterPayload, 'key'>>) => (chapterId: string, data: Omit<TAcademyChapterPayload, 'key'>) => {
  return axios({
    method: EMethod.put,
    url: `${baseUrl()}/chapters/${chapterId}`,
    data,
  })
}

/** 409 while lessons use the chapter. */
const deleteChapter = (axios: IFactoryFunctionRequest<unknown, any>) => (chapterId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${baseUrl()}/chapters/${chapterId}`,
  })
}

// Lessons

/** Each lesson carries `production`: its open produce or revise run, if any. */
const lessons = (axios: IFactoryFunctionRequest<TAcademyManagedLessonWithProduction[], any>) => (query: { status?: AcademyLessonStatus } = {}) => {
  return axios({
    method: EMethod.get,
    url: `${baseUrl()}/lessons`,
    query: withoutEmpty(query),
  })
}

/** 409 on a duplicate slug or when featureKeys overlap another lesson (`{ customMessage, overlaps }`). */
const createLesson = (axios: IFactoryFunctionRequest<IAcademyManagedLesson, TAcademyLessonPayload>) => (data: TAcademyLessonPayload) => {
  return axios({
    method: EMethod.post,
    url: `${baseUrl()}/lessons`,
    data,
  })
}

const updateLesson = (axios: IFactoryFunctionRequest<IAcademyManagedLesson, Omit<TAcademyLessonPayload, 'slug'>>) => (lessonId: string, data: Omit<TAcademyLessonPayload, 'slug'>) => {
  return axios({
    method: EMethod.put,
    url: `${baseUrl()}/lessons/${lessonId}`,
    data,
  })
}

const archiveLesson = (axios: IFactoryFunctionRequest<IAcademyManagedLesson, any>) => (lessonId: string) => {
  return axios({
    method: EMethod.post,
    url: `${baseUrl()}/lessons/${lessonId}/archive`,
  })
}

const unarchiveLesson = (axios: IFactoryFunctionRequest<IAcademyManagedLesson, any>) => (lessonId: string) => {
  return axios({
    method: EMethod.post,
    url: `${baseUrl()}/lessons/${lessonId}/unarchive`,
  })
}

// Revisions

/** Without vtt text nor media URLs: the preview reads GET /revisions/:id. Several statuses are sent comma-separated. */
const revisions = (axios: IFactoryFunctionRequest<IAcademyRevision[], any>) => ({ status, lessonId }: { status?: AcademyRevisionStatus | AcademyRevisionStatus[], lessonId?: string } = {}) => {
  return axios({
    method: EMethod.get,
    url: `${baseUrl()}/revisions`,
    query: withoutEmpty({ status: Array.isArray(status) ? status.join(',') : status, lessonId }),
  })
}

const revision = (axios: IFactoryFunctionRequest<TAcademyRevisionDetail, any>) => (revisionId: string) => {
  return axios({
    method: EMethod.get,
    url: `${baseUrl()}/revisions/${revisionId}`,
  })
}

const createRevision = (axios: IFactoryFunctionRequest<IAcademyRevision, TAcademyRevisionPayload>) => (data: TAcademyRevisionPayload) => {
  return axios({
    method: EMethod.post,
    url: `${baseUrl()}/revisions`,
    data,
  })
}

/** Only while the revision is producing, in review or waiting for changes; the content is scanned again. */
const updateRevision = (axios: IFactoryFunctionRequest<IAcademyRevision, TAcademyRevisionPayload>) => (revisionId: string, data: TAcademyRevisionPayload) => {
  return axios({
    method: EMethod.patch,
    url: `${baseUrl()}/revisions/${revisionId}`,
    data,
  })
}

/** Video, poster, image or vtt file of the revision (300 MB max); a vtt is kept inline as text. */
const uploadRevisionMedia = (axios: IFactoryFunctionRequest<IAcademyRevision, FormData>) => (revisionId: string, { file, ref, kind, lang, durationSec }: TAcademyMediaUpload) => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('ref', ref)
  formData.append('kind', kind)
  if (lang) formData.append('lang', lang)
  if (typeof durationSec === 'number' && Number.isFinite(durationSec)) formData.append('durationSec', String(durationSec))
  return axios({
    method: EMethod.post,
    url: `${baseUrl()}/revisions/${revisionId}/media`,
    data: formData,
  })
}

const submitRevision = (axios: IFactoryFunctionRequest<IAcademyRevision, any>) => (revisionId: string) => {
  return axios({
    method: EMethod.post,
    url: `${baseUrl()}/revisions/${revisionId}/submit`,
  })
}

/** 409 when it cannot be published: `{ customMessage, blockers }` (pages differ between fr and en, media missing...) or `{ customMessage, findings }`. */
const publishRevision = (axios: IFactoryFunctionRequest<IAcademyManagedLesson, TAcademyPublishPayload>) => (revisionId: string, data: TAcademyPublishPayload) => {
  return axios({
    method: EMethod.post,
    url: `${baseUrl()}/revisions/${revisionId}/publish`,
    data,
  })
}

const requestRevisionChanges = (axios: IFactoryFunctionRequest<IAcademyRevision, { comment: string }>) => (revisionId: string, data: { comment: string }) => {
  return axios({
    method: EMethod.post,
    url: `${baseUrl()}/revisions/${revisionId}/request-changes`,
    data,
  })
}

const rejectRevision = (axios: IFactoryFunctionRequest<IAcademyRevision, { reason: string }>) => (revisionId: string, data: { reason: string }) => {
  return axios({
    method: EMethod.post,
    url: `${baseUrl()}/revisions/${revisionId}/reject`,
    data,
  })
}

// Runs of the academy agent: the worker claims the queued ones, the management only creates and cancels them.

/** The latest runs first, with the weekly schedule and the last processed commit. */
const runs = (axios: IFactoryFunctionRequest<TAcademyRunsResponse, any>) => (query: { limit?: number } = {}) => {
  return axios({
    method: EMethod.get,
    url: `${baseUrl()}/runs`,
    query: withoutEmpty({ limit: query.limit === undefined ? undefined : String(query.limit) }),
  })
}

/** With the log tail. */
const run = (axios: IFactoryFunctionRequest<TAcademyRun, any>) => (runId: string) => {
  return axios({
    method: EMethod.get,
    url: `${baseUrl()}/runs/${runId}`,
  })
}

/** Queues a weekly run (202); 409 while another weekly run is queued or running. */
const startRun = (axios: IFactoryFunctionRequest<{ run: TAcademyRun }, { dryRun?: boolean }>) => (data: { dryRun?: boolean } = {}) => {
  return axios({
    method: EMethod.post,
    url: `${baseUrl()}/runs`,
    data,
  })
}

/** A queued run never starts; a running one stops at the worker's next heartbeat. */
const cancelRun = (axios: IFactoryFunctionRequest<{ run: TAcademyRun }, any>) => (runId: string) => {
  return axios({
    method: EMethod.post,
    url: `${baseUrl()}/runs/${runId}/cancel`,
  })
}

/** « Produire »: queues a produce run for the lesson (202). 409 when it is archived or already in production. */
const produceLesson = (axios: IFactoryFunctionRequest<{ run: TAcademyRun }, TAcademyProducePayload>) => (lessonId: string, data: TAcademyProducePayload) => {
  return axios({
    method: EMethod.post,
    url: `${baseUrl()}/lessons/${lessonId}/produce`,
    data,
  })
}

// The companies of the sandbox firm filmed for the academy get invented names (run by the sandbox server).

const sandboxCompanies = (axios: IFactoryFunctionRequest<TSandboxCompaniesReport, any>) => () => {
  return axios({
    method: EMethod.get,
    url: `${baseUrl()}/sandbox/companies`,
  })
}

const applySandboxCompanies = (axios: IFactoryFunctionRequest<TSandboxCompaniesReport, any>) => () => {
  return axios({
    method: EMethod.post,
    url: `${baseUrl()}/sandbox/companies`,
  })
}

export default (axios: AxiosInstance) => ({
  chapters: chapters(useAxios(axios)),
  createChapter: createChapter(useAxios(axios)),
  updateChapter: updateChapter(useAxios(axios)),
  deleteChapter: deleteChapter(useAxios(axios)),
  lessons: lessons(useAxios(axios)),
  createLesson: createLesson(useAxios(axios)),
  updateLesson: updateLesson(useAxios(axios)),
  archiveLesson: archiveLesson(useAxios(axios)),
  unarchiveLesson: unarchiveLesson(useAxios(axios)),
  revisions: revisions(useAxios(axios)),
  revision: revision(useAxios(axios)),
  createRevision: createRevision(useAxios(axios)),
  updateRevision: updateRevision(useAxios(axios)),
  uploadRevisionMedia: uploadRevisionMedia(useAxios(axios)),
  submitRevision: submitRevision(useAxios(axios)),
  publishRevision: publishRevision(useAxios(axios)),
  requestRevisionChanges: requestRevisionChanges(useAxios(axios)),
  rejectRevision: rejectRevision(useAxios(axios)),
  runs: runs(useAxios(axios)),
  run: run(useAxios(axios)),
  startRun: startRun(useAxios(axios)),
  cancelRun: cancelRun(useAxios(axios)),
  produceLesson: produceLesson(useAxios(axios)),
  sandboxCompanies: sandboxCompanies(useAxios(axios)),
  applySandboxCompanies: applySandboxCompanies(useAxios(axios)),
})
